import PropTypes from 'prop-types';
import React, { useEffect, useMemo } from 'react';
import { OSIcons } from '../../utils/OS';
import Alert, { ALERT_TYPES } from '../../components/atoms/Alert';
import AddFutureItems from '../containers/AddFutureItems';
import { translationPrefix } from '../utils';
import css from './AppSitePageStep.css';
import SearchableMultiSelect from './SearchableMultiSelect';
import StepCard from './StepCard';

const getFormattedSite = site => {
    const formattedPlatform = `${site.platform.charAt(0).toUpperCase()}${site.platform.slice(1)}`;
    const Icon = Object.entries(OSIcons).find(([key]) => key.toLowerCase() === site.platform.toLowerCase())?.[1];
    return {
        ...site,
        id: site.publicId,
        displayName: `${formattedPlatform} - ${site.publicId}`,
        Icon,
    };
};

const AppSitePageStep = ({
    apps = [],
    onChange = () => {},
    enableAddFuture = () => {},
    onStepValidationStatusChanged = () => {},
    translate = () => {},
    removedAppSites = [],
}) => {
    const selectedAppsCount = apps.filter(app => app.selected).length;
    const selectedAppSitesCount = apps.flatMap(app => app.sites.filter(site => site.selected)).length;
    const disabledAppsCount = apps.filter(app => app.disabled).length;
    const totalAvailableAppsCount = apps.length;

    const appSitePublicIds = useMemo(
        () =>
            apps
                .filter(app => !app.disabled)
                .flatMap(app => app.sites.filter(site => !site.disabled).map(site => site.publicId)),
        [apps]
    );
    const formattedApps = useMemo(
        () =>
            apps.map(app => ({
                ...app,
                subItems: app.sites.map(getFormattedSite),
                ...(app.icon && {
                    Icon: 'img',
                    iconProps: { src: app.icon, alt: app.name },
                }),
            })),
        [apps]
    );

    useEffect(() => {
        const isValid = selectedAppSitesCount > 0;
        onStepValidationStatusChanged({
            isValid,
            title: isValid
                ? ''
                : translate('STATIC.PAGES.TEAM_MEMBERS.SELECT_AT_LEAST_ONE_TOOLTIP', {
                      itemType: 'appsite',
                  }),
        });
    }, [onStepValidationStatusChanged, translate, selectedAppSitesCount]);

    const selectAll = value => {
        onChange(appSitePublicIds, value);
        if (value) {
            enableAddFuture();
        }
    };

    const onItemSelected = (selectedId, value, isAppSite = false) => {
        if (isAppSite) {
            onChange([selectedId], value);
        } else {
            const app = apps.find(({ id }) => id === selectedId);
            if (app) {
                onChange(
                    app.sites.map(site => site.publicId),
                    value
                );
            }
        }
    };

    return (
        <div className={css.container}>
            {removedAppSites.length > 0 && (
                <Alert
                    type={ALERT_TYPES.WARNING}
                    text={translate(`${translationPrefix}.APP_REMOVAL_WARNING`)}
                    className={css.appRemovalWarning}
                />
            )}
            <StepCard
                className={css.stepCard}
                title={`${translationPrefix}.MANAGE_PERMISSIONS_PAGE.TITLE.APPS`}
                titleTextReplace={{
                    count: selectedAppsCount,
                    total: totalAvailableAppsCount - disabledAppsCount,
                }}
                description={`${translationPrefix}.MANAGE_PERMISSIONS_PAGE.DESCRIPTION.APPS`}
            >
                <SearchableMultiSelect
                    className={css.multiSelectContainer}
                    allItems={formattedApps}
                    filteredItems={formattedApps}
                    onChange={onItemSelected}
                    selectAll={selectAll}
                    itemType="appsites"
                    searchPlaceholder={translate(`${translationPrefix}.PLACEHOLDER.APP_SEARCH`)}
                    sorted={false}
                />
            </StepCard>
            <AddFutureItems itemType="appsite" form="app_site_id" dimensionFilter className={css.addFutureItems} />
        </div>
    );
};

AppSitePageStep.propTypes = {
    apps: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.number.isRequired,
            name: PropTypes.string.isRequired,
            displayName: PropTypes.string.isRequired,
            icon: PropTypes.string.isRequired,
            selected: PropTypes.bool.isRequired,
            sites: PropTypes.arrayOf(
                PropTypes.shape({
                    id: PropTypes.number.isRequired,
                    publicId: PropTypes.string.isRequired,
                    platform: PropTypes.string.isRequired,
                    icon: PropTypes.string.isRequired,
                })
            ),
        })
    ),
    onChange: PropTypes.func,
    enableAddFuture: PropTypes.func,
    onStepValidationStatusChanged: PropTypes.func,
    translate: PropTypes.func,
    removedAppSites: PropTypes.arrayOf(PropTypes.string),
};

export default AppSitePageStep;
